import { RackingArea } from '@thive/fddm-types';
import { Stack } from '@mui/system';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useHasOmniVehicles } from '@/modules/vehicles/hooks/useHasOmniVehicles';
import { CrabSettingInput } from '../common/CrabSettingInput';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';
import { getConnectionsWithSmoothHeading } from '../common/crabSettingConnections';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { usePropertyPanelConfirm } from '@/modules/common/components/usePropertyPanelConfirm';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';

type Props = {
  selectedRackingAreas: RackingArea[];
};

function RackingPoiPreferencesComponent({ selectedRackingAreas }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);
  const { confirm, Dialog } = usePropertyPanelConfirm();
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const hasOmniVehicles = useHasOmniVehicles();
  // Crab setting only applies to one-sided alongside racks
  const crabRacks = useMemo(
    () => selectedRackingAreas.filter(({ rackType }) => rackType === 'ONE_SIDED_ALONGSIDE'),
    [selectedRackingAreas],
  );
  // null (no option selected) when the racks have different crab settings
  const crabSetting = useMemo(
    () => getCommonNumber(crabRacks.map((area) => area.crabSetting ?? 0)),
    [crabRacks],
  );
  // Shown when any selected rack is one-sided alongside; only editable when all of them are
  const showCrabSetting = hasOmniVehicles && crabRacks.length > 0;
  const isCrabPartialSelection = crabRacks.length !== selectedRackingAreas.length;

  /**
@description please read!
We are not supporting parking on racks for now. When we do, be sure to update the input so it supports multi- edit! I did not include that right now.
*/

  // const showParkingSetting = false;
  // const canBeUsedForParking = useMemo(() => {
  //   const values = selectedRackingAreas.map(area => area.canBeUsedForParking)
  //   return getCommonBoolean(values);
  // }, []);

  const handleCrabChange = useCallback(
    async (value: number) => {
      const smoothHeading = value !== 0 ? true : fddmSvc.getPreference('connection.smoothHeading');
      const connectionsToUpdate = getConnectionsWithSmoothHeading(
        useFloorplanDesignStore
          .getState()
          .interAreaConnectionActions.getRelatedTo(selectedRackingAreas.map(({ id }) => id)),
        smoothHeading,
      );

      // The connections aren't visible here, so ask before changing them
      if (connectionsToUpdate.length > 0) {
        const confirmed = await confirm(
          t('interface:properties.area.crab_setting_confirm.label'),
          t('interface:properties.area.crab_setting_confirm.message', {
            count: connectionsToUpdate.length,
          }),
        );
        if (!confirmed) return;
      }

      const updatedAreas = createEmptyAreas();
      selectedRackingAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          crabSetting: value,
        });
      });

      historySvc.batch(() => {
        fddmSvc.setPreference('rackingArea.crabSetting', value);
        fddmSvc.updateAreas(updatedAreas);
        if (connectionsToUpdate.length > 0) {
          fddmSvc.updateInterAreaConnections(connectionsToUpdate);
        }
      });
    },
    [confirm, fddmSvc, historySvc, selectedRackingAreas, t],
  );

  // const onParkingChange = useCallback(
  //   (_, newValue: boolean) => {
  //     fddmSvc.setPreference('rackingArea.canBeUsedForParking', newValue);

  //     const parkingExtension = newValue
  //       ? {
  //           priority: fddmSvc.getPreference('rackingArea.parkingExtension.priority'),
  //         }
  //       : undefined;

  //     fddmSvc.updateArea({
  //       ...selectedRackingArea,
  //       canBeUsedForParking: newValue,
  //       parkingExtension,
  //     });
  //   },
  //   [fddmSvc, selectedRackingArea],
  // );

  // const onParkingPriorityChange = useCallback(
  //   (newValue: number) => {
  //     if (Number.isNaN(newValue)) return;

  //     fddmSvc.setPreference('rackingArea.parkingExtension.priority', newValue);

  //     fddmSvc.updateArea({
  //       ...selectedRackingArea,
  //       parkingExtension: {
  //         priority: newValue,
  //       },
  //     });
  //   },
  //   [fddmSvc, selectedRackingArea],
  // );

  return (
    <Stack spacing={1}>
      {showCrabSetting && (
        <>
          <Dialog anchor={ref} offset={[0, 100]} />
          <div ref={ref}>
            <PropertiesLabel i18nkey='interface:properties.area.crab_setting'>
              <DisabledReasonTooltip
                reasonKey={
                  isCrabPartialSelection
                    ? 'interface:properties.area.rack.partial_selection.only_one_sided_alongside'
                    : undefined
                }
              >
                <CrabSettingInput
                  value={crabSetting}
                  disabled={isCrabPartialSelection}
                  onChange={handleCrabChange}
                />
              </DisabledReasonTooltip>
            </PropertiesLabel>
          </div>
        </>
      )}
      {/* {showParkingSetting && (
        <PropertiesLabel i18nkey='interface:properties.area.can_be_used_for_parking'>
          <Switch
            checked={selectedRackingArea.canBeUsedForParking}
            onChange={onParkingChange}
          />
        </PropertiesLabel>
      )}
      {showParkingSetting &&
        selectedRackingArea.canBeUsedForParking === true &&
        selectedRackingArea.parkingExtension !== undefined && (
          <PropertiesLabel i18nkey='interface:properties.area.priority'>
            <NumberInput
              value={selectedRackingArea.parkingExtension.priority}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onParkingPriorityChange}
            />
          </PropertiesLabel>
        )} */}
    </Stack>
  );
}

export const RackingPoiPreferences = memo(RackingPoiPreferencesComponent);
